import { Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { getTimeOfDay, greetingEnum } from 'constants/global.constants';
import { getAllAnalyticsPendingRequests } from 'services/pending-requests.service';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { SearchComponent } from 'components/SearchComponent/SearchComponent';
import {
	beginBeneficiaryCubeRequests,
	getError as getBalanceErrorMessage,
	invalidateBeneficiaryCubeRequests,
	setError as setBalanceError,
} from 'state/reducers/balance.reducer';
import {
	getError as getPendingRequestsErrorMessage,
	setError as setPendingRequestError,
} from 'state/reducers/pending-requests.reducer';
import { WarningDialog } from 'components/WarningDialog';
import { DashboardBalances } from 'sections/MainDashboard/DashboardBalances';
import { FilterComponent } from 'sections/MainDashboard/FilterComponent';
import {
	getAccountBalances,
	getAverageIncomeFromCustomer,
	getBeneficiaryResponseTimeSummary,
	getBeneficiaryStatusSummary,
	getConversionFees,
	getCurrenciesToDisplay,
	getPaymentFees,
	getPendingBalances,
	getPlanFees,
} from 'services/balances.service';
import { currenciesToDisplay } from 'constants/balance.constants';
import { getBeneficiaryDatesRange, setFilters } from 'state/reducers/dashboard.reducer';
import { getAltshulerEmployees } from 'state/reducers/company.reducers';
import _ from 'lodash';

export const MainDashboard = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const [pageGreeting, setPageGreeting] = useState('');
	const { firstName } = useAppSelector(getUserDetails);
	const pagePermission = hasPermission([permissionEnums.MENU_DASHBOARD]);
	const balancePermission = hasPermission([permissionEnums.DASHBOARD_BALANCES]);
	const searchPermission = hasPermission([permissionEnums.FREE_TEXT_SEARCH_DATABASE]);
	const pagePrefix = 'page.dashboard.search.';
	const balanceErrorMessage = useAppSelector(getBalanceErrorMessage);
	const pendingRequestsErrorMessage = useAppSelector(getPendingRequestsErrorMessage);
	const filters = useAppSelector((state) => state.dashboard.filters);
	const beneficiaryDatesRange = useAppSelector(getBeneficiaryDatesRange);
	const user = useAppSelector(getUserDetails);
	const employees = useAppSelector(getAltshulerEmployees(true));

	useEffect(() => {
		if (pagePermission) {
			//get all data from here
			setTimeOfDay();
			dispatch(getCurrenciesToDisplay(currenciesToDisplay));
			dispatch(getAccountBalances(filters, false)); //load from filter component
			dispatch(getPendingBalances(filters, false));
			dispatch(getPaymentFees(filters, false));
			dispatch(getConversionFees(filters, false));
			dispatch(getPlanFees(filters, false));
			dispatch(getAverageIncomeFromCustomer(filters));
		}
	}, [pagePermission, filters]);

	useEffect(() => {
		if (!balancePermission) return;

		dispatch(beginBeneficiaryCubeRequests());
		dispatch(getBeneficiaryStatusSummary(beneficiaryDatesRange));
		dispatch(getBeneficiaryResponseTimeSummary(beneficiaryDatesRange));

		return () => {
			dispatch(invalidateBeneficiaryCubeRequests());
		};
	}, [balancePermission, beneficiaryDatesRange, dispatch]);

	const setTimeOfDay = () => {
		switch (getTimeOfDay()) {
			case greetingEnum.MORNING:
				setPageGreeting(`${t('greeting.morning')}`);
				break;
			case greetingEnum.NOON:
				setPageGreeting(`${t('greeting.noon')}`);
				break;
			case greetingEnum.AFTERNOON:
				setPageGreeting(`${t('greeting.afternoon')}`);
				break;
			case greetingEnum.EVENING:
				setPageGreeting(`${t('greeting.evening')}`);
				break;
			default:
				setPageGreeting(`${t('greeting.hello')}`);
				break;
		}
	};

	const openDialog = useMemo(() => {
		return balanceErrorMessage.exist || pendingRequestsErrorMessage.exist;
	}, [balanceErrorMessage, pendingRequestsErrorMessage]);

	const currentSetErrorMethod: any = useMemo(() => {
		if (balanceErrorMessage.exist) return setBalanceError;
		if (pendingRequestsErrorMessage.exist) return setPendingRequestError;
		return null;
	}, [balanceErrorMessage, pendingRequestsErrorMessage]);

	const currentErrorMessage: any = useMemo(() => {
		if (!openDialog) return null;
		if (balanceErrorMessage.exist) return balanceErrorMessage;
		if (pendingRequestsErrorMessage.exist) return pendingRequestsErrorMessage;
		return null;
	}, [openDialog, balanceErrorMessage, pendingRequestsErrorMessage]);

	useEffect(() => {
		if (!user || !employees.altshulerDealers?.length) {
			return;
		}
		const matchedEmployee = _.find(employees.altshulerDealers, { boUserId: user.id });
		const updatedValues = { ...filters, dealer: matchedEmployee?.id || null };
		dispatch(setFilters(updatedValues));
	}, [user]);

	return (
		<Fragment>
			<Page title="">
				<Stack spacing={7}>
					<WarningDialog
						open={openDialog}
						subTitle={currentErrorMessage?.text}
						handleCancel={() => {
							currentSetErrorMethod && dispatch(currentSetErrorMethod({ exist: false }));
						}}
					/>
					<Stack spacing={4}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography variant="pageTitle">
								{pageGreeting}, {firstName}
							</Typography>
						</Box>

						{searchPermission && (
							<Stack spacing={3}>
								<Stack spacing={4}>
									<FilterComponent />
									<Divider
										orientation="horizontal"
										flexItem
										sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
									/>
								</Stack>
							</Stack>
						)}
					</Stack>
					<Stack></Stack>
					{balancePermission && (
						<Stack spacing={4}>
							<DashboardBalances />
							<Divider
								orientation="horizontal"
								flexItem
								sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
							/>
						</Stack>
					)}
				</Stack>
			</Page>
		</Fragment>
	);
};
